import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAuthenticated, getAuthHeaders } from '../utils/auth';
import { API_ENDPOINTS } from '../config/api';
import './JobManagement.css';

// ─── Toast helper ────────────────────────────────────────────────
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  return <div className={`job-toast ${type}`}>{message}</div>;
}

// ─── Main Component ──────────────────────────────────────────────
export default function JobManagement() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [activeCount, setActiveCount] = useState(0);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [formData, setFormData] = useState({
    positionId: '', company: '', title: '', qualification: '', description: '', location: '', experienceRequired: '', technology: '', jobType: 'Full-time', applyLink: '', active: true
  });
  const [saving, setSaving] = useState(false);

  // Excel upload
  const [excelFile, setExcelFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  // Confirm
  const [confirmAction, setConfirmAction] = useState(null);

  // Search & Filter
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive'

  // Bulk select
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Toast
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => setToast({ message, type });

  // ─── Auth check ────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated()) {
      const tm = setTimeout(() => {
        if (!isAuthenticated()) navigate('/signup');
        else setAuthChecked(true);
      }, 100);
      return () => clearTimeout(tm);
    }
    setAuthChecked(true);
  }, [navigate]);

  // ─── Fetch jobs (admin – all including inactive) ───────────
  const fetchJobs = useCallback(async (p = 0) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_ENDPOINTS.JOBS.ADMIN_LIST}?page=${p}&size=50`, {
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (res.status === 403) throw new Error('Admin access required');
      if (!res.ok) throw new Error('Failed to load jobs');
      const data = await res.json();
      setJobs(data.jobs || []);
      setPage(data.currentPage || 0);
      setTotalPages(data.totalPages || 1);
      setTotalElements(data.totalElements || 0);
      setActiveCount(data.activeCount || 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (authChecked) fetchJobs(0); }, [authChecked, fetchJobs]);

  // ─── Open create / edit modal ──────────────────────────────
  const openCreateModal = () => {
    setEditingJob(null);
    setFormData({ positionId: '', company: '', title: '', qualification: '', description: '', location: '', experienceRequired: '', technology: '', jobType: 'Full-time', applyLink: '', active: true });
    setShowModal(true);
  };
  const openEditModal = (job) => {
    setEditingJob(job);
    setFormData({
      positionId: job.positionId || '',
      company: job.company || '',
      title: job.title || '',
      qualification: job.qualification || '',
      description: job.description || '',
      location: job.location || '',
      experienceRequired: job.experienceRequired || '',
      technology: job.technology || '',
      jobType: job.jobType || 'Full-time',
      applyLink: job.applyLink || '',
      active: job.active !== false,
    });
    setShowModal(true);
  };

  // ─── Excel upload ──────────────────────────────────────────
  const handleExcelUpload = async () => {
    if (!excelFile) {
      showToast('Please select an Excel file first', 'error');
      return;
    }
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append('file', excelFile);
      const authHeaders = getAuthHeaders();
      // Remove Content-Type — browser will set multipart boundary automatically
      delete authHeaders['Content-Type'];
      const res = await fetch(API_ENDPOINTS.JOBS.ADMIN_UPLOAD_EXCEL, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders,
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Upload failed');
      }
      const data = await res.json();
      setUploadResult(data);
      showToast(`Uploaded ${data.successCount} of ${data.totalRows} jobs`);
      setExcelFile(null);
      // Reset the file input
      const fileInput = document.getElementById('excel-file-input');
      if (fileInput) fileInput.value = '';
      fetchJobs(0);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setUploading(false);
    }
  };

  // ─── Save (create or update) ──────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      const url = editingJob
        ? API_ENDPOINTS.JOBS.ADMIN_UPDATE(editingJob.id)
        : API_ENDPOINTS.JOBS.ADMIN_CREATE;
      const method = editingJob ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify(formData),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to save job');
      }
      showToast(editingJob ? 'Job updated successfully' : 'Job created successfully');
      setShowModal(false);
      fetchJobs(page);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  // ─── Delete ────────────────────────────────────────────────
  const handleDelete = async (job) => {
    try {
      const res = await fetch(API_ENDPOINTS.JOBS.ADMIN_DELETE(job.id), {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to delete job');
      showToast('Job deleted successfully');
      fetchJobs(page);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setConfirmAction(null);
    }
  };

  // ─── Toggle active ────────────────────────────────────────
  const toggleActive = async (job) => {
    try {
      const res = await fetch(API_ENDPOINTS.JOBS.ADMIN_UPDATE(job.id), {
        method: 'PUT',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({ active: !job.active }),
      });
      if (!res.ok) throw new Error('Failed to update job');
      showToast(job.active ? 'Job deactivated' : 'Job activated');
      fetchJobs(page);
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  // ─── Filtered jobs (client-side search + status filter) ────
  const filteredJobs = jobs.filter(job => {
    // Status filter
    if (statusFilter === 'active' && !job.active) return false;
    if (statusFilter === 'inactive' && job.active) return false;
    // Search filter
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      return (
        (job.positionId || '').toLowerCase().includes(q) ||
        (job.company || '').toLowerCase().includes(q) ||
        (job.title || '').toLowerCase().includes(q) ||
        (job.location || '').toLowerCase().includes(q) ||
        (job.technology || '').toLowerCase().includes(q) ||
        (job.qualification || '').toLowerCase().includes(q) ||
        (job.jobType || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  // ─── Bulk select helpers ───────────────────────────────────
  const toggleSelectJob = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredJobs.length && filteredJobs.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredJobs.map(j => j.id)));
    }
  };

  // Clear selection when search/filter changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [searchTerm, statusFilter, page]);

  // ─── Bulk delete ───────────────────────────────────────────
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    try {
      const res = await fetch(API_ENDPOINTS.JOBS.ADMIN_BULK_DELETE, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify([...selectedIds]),
      });
      if (!res.ok) throw new Error('Bulk delete failed');
      const data = await res.json();
      showToast(`${data.deletedCount} job(s) deleted successfully`);
      setSelectedIds(new Set());
      setConfirmAction(null);
      fetchJobs(page);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setBulkDeleting(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────
  if (!authChecked) return null;

  if (loading) {
    return (
      <div className="job-mgmt-page">
        <div className="job-mgmt-container">
          <div className="job-loading-spinner">
            <div className="spinner" />
            <p>Loading jobs...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="job-mgmt-page">
        <div className="job-mgmt-container">
          <div className="job-error">
            <span className="error-icon">⚠️</span>
            <p>{error}</p>
            <button onClick={() => fetchJobs(0)} className="btn-retry">Retry</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="job-mgmt-page">
      <div className="job-mgmt-container">
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

        {/* Header */}
        <div className="job-mgmt-header">
          <div>
            <h1>💼 Job Positions</h1>
            <p className="admin-note">Admin View – Manage Job Listings</p>
          </div>
          <button className="btn-create-job" onClick={openCreateModal}>
            ➕ Post New Job
          </button>
        </div>

        {/* Excel Upload Section */}
        <div className="excel-upload-section">
          <div className="excel-upload-card">
            <div className="excel-upload-header">
              <span className="excel-icon">📊</span>
              <div>
                <h3>Bulk Upload via Excel</h3>
                <p>Upload .xlsx file with headers: Sl.no, Company, Role, Qualification, Experience, Location, Job Type, Technology, Required Skills or JD, Send cv</p>
              </div>
            </div>
            <div className="excel-upload-controls">
              <input type="file" id="excel-file-input" accept=".xlsx,.xls"
                onChange={e => { setExcelFile(e.target.files[0]); setUploadResult(null); }}
              />
              <button className="btn-upload-excel" onClick={handleExcelUpload} disabled={uploading || !excelFile}>
                {uploading ? '⏳ Uploading...' : '📤 Upload Excel'}
              </button>
            </div>
            {uploadResult && (
              <div className="excel-upload-result">
                <span className="upload-stat success">✅ {uploadResult.successCount} uploaded</span>
                <span className="upload-stat total">📋 {uploadResult.totalRows} total rows</span>
                {uploadResult.errorCount > 0 && (
                  <span className="upload-stat errors">❌ {uploadResult.errorCount} errors</span>
                )}
                {uploadResult.errors && uploadResult.errors.length > 0 && (
                  <div className="upload-errors-list">
                    {uploadResult.errors.map((err, i) => (
                      <div key={i} className="upload-error-item">Row {err.row}: {err.error}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="job-stats">
          <div className="job-stat-card">
            <div className="stat-icon">📋</div>
            <div className="stat-number">{totalElements}</div>
            <div className="stat-label">Total Jobs</div>
          </div>
          <div className="job-stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-number">{activeCount}</div>
            <div className="stat-label">Active</div>
          </div>
          <div className="job-stat-card">
            <div className="stat-icon">⏸️</div>
            <div className="stat-number">{totalElements - activeCount}</div>
            <div className="stat-label">Inactive</div>
          </div>
        </div>

        {/* Search & Filter Bar */}
        <div className="job-filter-bar">
          <div className="filter-left">
            <div className="search-input-wrapper">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                className="job-search-input"
                placeholder="Search by company, role, location, technology..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button className="search-clear" onClick={() => setSearchTerm('')}>✕</button>
              )}
            </div>
            <div className="status-filter-group">
              <button className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`} onClick={() => setStatusFilter('all')}>All</button>
              <button className={`filter-btn ${statusFilter === 'active' ? 'active' : ''}`} onClick={() => setStatusFilter('active')}>Active</button>
              <button className={`filter-btn ${statusFilter === 'inactive' ? 'active' : ''}`} onClick={() => setStatusFilter('inactive')}>Inactive</button>
            </div>
          </div>
          <div className="filter-right">
            <span className="filter-count">{filteredJobs.length} of {jobs.length} jobs</span>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedIds.size > 0 && (
          <div className="bulk-actions-bar">
            <span className="bulk-count">✓ {selectedIds.size} job{selectedIds.size > 1 ? 's' : ''} selected</span>
            <button
              className="btn-bulk-delete"
              onClick={() => setConfirmAction({ type: 'bulk-delete' })}
              disabled={bulkDeleting}
            >
              🗑️ Delete Selected ({selectedIds.size})
            </button>
            <button className="btn-bulk-clear" onClick={() => setSelectedIds(new Set())}>
              ✕ Clear Selection
            </button>
          </div>
        )}

        {/* Jobs Table */}
        {filteredJobs.length === 0 ? (
          <div className="job-empty">
            <span style={{ fontSize: 48 }}>💼</span>
            <h3>{searchTerm || statusFilter !== 'all' ? 'No matching jobs found' : 'No jobs posted yet'}</h3>
            <p>{searchTerm || statusFilter !== 'all' ? 'Try adjusting your search or filter.' : 'Post your first job to get started.'}</p>
            {!searchTerm && statusFilter === 'all' && (
              <button className="btn-create-job" onClick={openCreateModal}>➕ Post New Job</button>
            )}
          </div>
        ) : (
          <div className="job-table-wrapper">
            <table className="job-table">
              <thead>
                <tr>
                  <th className="select-col">
                    <span
                      className={`row-selector ${selectedIds.size === filteredJobs.length && filteredJobs.length > 0 ? 'checked' : ''}`}
                      onClick={toggleSelectAll}
                      title="Select all"
                    />
                  </th>
                  <th>Sl.no</th>
                  <th>Company</th>
                  <th>Role</th>
                  <th>Qualification</th>
                  <th>Location</th>
                  <th>Technology</th>
                  <th>Experience</th>
                  <th>Job Type</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredJobs.map(job => (
                  <tr key={job.id} className={`${!job.active ? 'inactive-row' : ''} ${selectedIds.has(job.id) ? 'selected-row' : ''}`}>
                    <td className="select-col">
                      <span
                        className={`row-selector ${selectedIds.has(job.id) ? 'checked' : ''}`}
                        onClick={() => toggleSelectJob(job.id)}
                      />
                    </td>
                    <td><span className="position-badge">{job.positionId}</span></td>
                    <td className="company-cell">{job.company || '—'}</td>
                    <td className="title-cell">{job.title}</td>
                    <td className="qual-cell">{job.qualification || '—'}</td>
                    <td><span className="location-tag">📍 {job.location}</span></td>
                    <td className="tech-cell">{job.technology || '—'}</td>
                    <td>{job.experienceRequired}</td>
                    <td><span className="type-badge">{job.jobType || 'Full-time'}</span></td>
                    <td>
                      <span className={`status-badge ${job.active ? 'active' : 'inactive'}`}>
                        {job.active ? '🟢 Active' : '🔴 Inactive'}
                      </span>
                    </td>
                    <td className="date-cell">
                      {job.createdAt ? new Date(job.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    </td>
                    <td className="actions-cell">
                      <button className="btn-action btn-edit" onClick={() => openEditModal(job)} title="Edit">✏️</button>
                      <button className="btn-action btn-toggle" onClick={() => toggleActive(job)} title={job.active ? 'Deactivate' : 'Activate'}>
                        {job.active ? '⏸️' : '▶️'}
                      </button>
                      <button className="btn-action btn-delete" onClick={() => setConfirmAction({ type: 'delete', job })} title="Delete">🗑️</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="job-pagination">
            <button disabled={page === 0} onClick={() => fetchJobs(0)} title="First page">⏮</button>
            <button disabled={page === 0} onClick={() => fetchJobs(page - 1)}>← Prev</button>
            <div className="page-numbers">
              {Array.from({ length: totalPages }, (_, i) => i)
                .filter(i => i === 0 || i === totalPages - 1 || Math.abs(i - page) <= 1)
                .reduce((acc, i, idx, arr) => {
                  if (idx > 0 && i - arr[idx - 1] > 1) acc.push('...');
                  acc.push(i);
                  return acc;
                }, [])
                .map((item, idx) =>
                  item === '...' ? (
                    <span key={`dots-${idx}`} className="page-dots">...</span>
                  ) : (
                    <button
                      key={item}
                      className={`page-num ${item === page ? 'active' : ''}`}
                      onClick={() => fetchJobs(item)}
                    >
                      {item + 1}
                    </button>
                  )
                )}
            </div>
            <button disabled={page >= totalPages - 1} onClick={() => fetchJobs(page + 1)}>Next →</button>
            <button disabled={page >= totalPages - 1} onClick={() => fetchJobs(totalPages - 1)} title="Last page">⏭</button>
          </div>
        )}

        {/* Create / Edit Modal */}
        {showModal && (
          <div className="job-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
            <div className="job-modal">
              <button className="job-modal-close" onClick={() => setShowModal(false)}>✕</button>
              <h2>{editingJob ? '✏️ Edit Job' : '➕ Post New Job'}</h2>

              <div className="job-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Sl.no (Position ID)</label>
                    <input type="text" placeholder="e.g. 1, SWE-001"
                      value={formData.positionId}
                      onChange={e => setFormData({ ...formData, positionId: e.target.value })}
                      disabled={!!editingJob}
                    />
                  </div>
                  <div className="form-group">
                    <label>Company</label>
                    <input type="text" placeholder="e.g. BlackKite AI"
                      value={formData.company}
                      onChange={e => setFormData({ ...formData, company: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Role *</label>
                    <input type="text" placeholder="e.g. Senior Software Engineer"
                      value={formData.title}
                      onChange={e => setFormData({ ...formData, title: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Qualification</label>
                    <input type="text" placeholder="e.g. B.Tech / M.Tech, MBA"
                      value={formData.qualification}
                      onChange={e => setFormData({ ...formData, qualification: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Required Skills or JD *</label>
                  <textarea rows={5} placeholder="Enter required skills or job description..."
                    value={formData.description}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Location</label>
                    <input type="text" placeholder="e.g. Bangalore, India / Remote"
                      value={formData.location}
                      onChange={e => setFormData({ ...formData, location: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Experience *</label>
                    <input type="text" placeholder="e.g. 3-5 years"
                      value={formData.experienceRequired}
                      onChange={e => setFormData({ ...formData, experienceRequired: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Technology</label>
                    <input type="text" placeholder="e.g. Java, Spring Boot, React"
                      value={formData.technology}
                      onChange={e => setFormData({ ...formData, technology: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Job Type</label>
                    <select value={formData.jobType} onChange={e => setFormData({ ...formData, jobType: e.target.value })}>
                      <option value="Full-time">Full-time</option>
                      <option value="Part-time">Part-time</option>
                      <option value="Contract">Contract</option>
                      <option value="Internship">Internship</option>
                      <option value="Remote">Remote</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Send cv (Apply Link) *</label>
                  <input type="text" placeholder="email or URL e.g. hr@company.com / https://..."
                    value={formData.applyLink}
                    onChange={e => setFormData({ ...formData, applyLink: e.target.value })}
                  />
                </div>

                <div className="form-group form-checkbox">
                  <label>
                    <input type="checkbox" checked={formData.active}
                      onChange={e => setFormData({ ...formData, active: e.target.checked })}
                    />
                    Active (visible to users)
                  </label>
                </div>

                <div className="form-actions">
                  <button className="btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                  <button className="btn-save" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving...' : editingJob ? 'Update Job' : 'Post Job'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Confirm Delete Modal */}
        {confirmAction && (
          <div className="job-modal-overlay" onClick={(e) => e.target === e.currentTarget && setConfirmAction(null)}>
            <div className="job-modal job-confirm-modal">
              <div className="confirm-icon">🗑️</div>
              {confirmAction.type === 'bulk-delete' ? (
                <>
                  <h3>Delete {selectedIds.size} Job{selectedIds.size > 1 ? 's' : ''}?</h3>
                  <p>Are you sure you want to delete <strong>{selectedIds.size} selected job{selectedIds.size > 1 ? 's' : ''}</strong>?</p>
                  <p className="confirm-warning">This action cannot be undone.</p>
                  <div className="confirm-actions">
                    <button className="btn-cancel" onClick={() => setConfirmAction(null)}>Cancel</button>
                    <button className="btn-delete-confirm" onClick={handleBulkDelete} disabled={bulkDeleting}>
                      {bulkDeleting ? '⏳ Deleting...' : `Delete ${selectedIds.size} Job${selectedIds.size > 1 ? 's' : ''}`}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h3>Delete Job?</h3>
                  <p>Are you sure you want to delete <strong>{confirmAction.job.positionId} – {confirmAction.job.title}</strong>?</p>
                  <p className="confirm-warning">This action cannot be undone.</p>
                  <div className="confirm-actions">
                    <button className="btn-cancel" onClick={() => setConfirmAction(null)}>Cancel</button>
                    <button className="btn-delete-confirm" onClick={() => handleDelete(confirmAction.job)}>Delete</button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
